import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  Typography,
  InputAdornment,
  Tooltip,
  Tabs,
  Tab,
  Pagination,
  Stack,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { MUI_ICON_NAMES } from 'src/generated/mui-icons';
import { SelectableTile } from './SelectableTile.styles';
import { UploadDropArea, VisuallyHiddenInput, BhiDialog } from 'src/UIKit';
import { SafeIcon } from 'src/components/SafeIcon/SafeIcon';
import { primaryActionButtonSx } from '../../helpers';

interface IconSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectIcon: (iconName: string) => void;
  onUploadIcon: (file: File) => void;
}

export const IconSelectorModal: React.FC<IconSelectorModalProps> = ({
  isOpen,
  onClose,
  onSelectIcon,
  onUploadIcon,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIcon, setSelectedIcon] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const iconsPerPage = 48;

  const filteredIcons = useMemo(() => {
    if (!searchTerm) return MUI_ICON_NAMES;

    return MUI_ICON_NAMES.filter(icon =>
      icon.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredIcons.length / iconsPerPage));
  const pagedIcons = useMemo(() => {
    const start = (page - 1) * iconsPerPage;
    return filteredIcons.slice(start, start + iconsPerPage);
  }, [filteredIcons, page]);

  useEffect(() => {
    if (!selectedFile) {
      setPreviewUrl(null);
      return;
    }

    const objectUrl = URL.createObjectURL(selectedFile);
    setPreviewUrl(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [selectedFile]);

  const handleSelectIcon = () => {
    if (selectedIcon) {
      onSelectIcon(selectedIcon);
      onClose();
      setSelectedIcon(null);
      setSearchTerm('');
      setPage(1);
    }
  };

  const handleUploadIcon = () => {
    if (!selectedFile) return;
    onUploadIcon(selectedFile);
    onClose();
  };

  const handleClose = () => {
    onClose();
    setActiveTab(0);
    setSelectedIcon(null);
    setSearchTerm('');
    setPage(1);
    setSelectedFile(null);
  };

  const handleIconClick = (iconName: string) => {
    setSelectedIcon(iconName);
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(1);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setSelectedFile(file);
  };

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);
    const file = event.dataTransfer.files?.[0] ?? null;
    if (file) {
      setSelectedFile(file);
    }
  }, []);

  return (
    <BhiDialog
      isOpen={isOpen}
      onClose={handleClose}
      maxWidth="sm"
      title="Select Icon"
      paperSx={{ width: 600, height: 680, display: 'flex', flexDirection: 'column' }}
    >
      <DialogContent sx={{ pb: 0, pt: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
          <Tab label="Pick Icon" />
          <Tab label="Upload Icon" />
        </Tabs>

        {activeTab === 0 && (
          <>
            <Box mb={1}>
              <TextField
                fullWidth
                placeholder="Search icons..."
                value={searchTerm}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                variant="outlined"
              />
            </Box>

            <Box height={360} overflow="auto" border={1} borderColor="divider" borderRadius={1}>
              <Grid container spacing={1} columns={{ xs: 4, sm: 6, md: 8 }}>
                {pagedIcons.map((icon) => {
                  const isSelected = selectedIcon === icon;

                  return (
                    <Grid item xs={1} key={icon}>
                      <Tooltip title={icon} arrow placement="top">
                        <SelectableTile selected={isSelected} onClick={() => handleIconClick(icon)}>
                          <SafeIcon name={icon} fontSize="small" />
                        </SelectableTile>
                      </Tooltip>
                    </Grid>
                  );
                })}
              </Grid>

              {filteredIcons.length === 0 && (
                <Box textAlign="center" py={4}>
                  <Typography variant="body2" color="text.secondary">
                    No icons found matching &quot;{searchTerm}&quot;
                  </Typography>
                </Box>
              )}
            </Box>

            {filteredIcons.length > 0 && (
              <Stack direction="row" justifyContent="center" mt={2} minHeight={40}>
                <Pagination count={totalPages} page={page} onChange={handlePageChange} color="primary" />
              </Stack>
            )}
          </>
        )}

        {activeTab === 1 && (
          <UploadDropArea
            centered
            component="label"
            sx={{ height: 400 }}
            className={isDragOver ? 'dragover' : ''}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {previewUrl ? (
              <Box
                component="img"
                src={previewUrl}
                alt="Selected icon preview"
                sx={{ objectFit: 'contain' }}
              />
            ) : (
              <>
                <CloudUploadIcon />
                <Typography variant="h6">Drag and drop your icon here</Typography>
                <Typography variant="body2" color="text.secondary">
                  or click to browse
                </Typography>
              </>
            )}
            <VisuallyHiddenInput type="file" accept="image/*" onChange={handleFileChange} />
          </UploadDropArea>
        )}
      </DialogContent>

      <DialogActions sx={{ gap: 0 }}>
        {activeTab === 0 && (
          <Button
            variant="contained"
            onClick={handleSelectIcon}
            disabled={!selectedIcon}
            sx={primaryActionButtonSx}
          >
            Select Icon
          </Button>
        )}
        {activeTab === 1 && (
          <Button
            variant="contained"
            onClick={handleUploadIcon}
            disabled={!selectedFile}
            sx={primaryActionButtonSx}
          >
            Select Icon
          </Button>
        )}
      </DialogActions>
    </BhiDialog>
  );
};
